import { TemplateQueryResponse } from '@common/types';
import {
  FAVORITES_ADD_API_URL,
  FAVORITES_COUNT_API_URL,
  FAVORITES_QUERY_API_URL,
  FAVORITES_REMOVE_API_URL,
  FAVORITES_TEMPLATES_API_URL,
} from '@utils/constants';

export type Favorite = {
  id?: string | null;
  entity?: { entityId?: string; entityType?: string };
  createdDate?: Date | null;
};

type FavoritesResponse = {
  favorites?: Favorite[];
  pagingMetadata?: {
    hasNext?: boolean | null;
    cursors?: { next?: string | null };
  };
};

type HttpClient = {
  post<T>(url: string, body?: any): Promise<{ data: T }>;
};

const FAVORITE_IDS_PAGE_SIZE = 100;
export const FAVORITE_TEMPLATES_PAGE_SIZE = 12;

const favoriteTemplatesSort = [
  { fieldName: 'createdDate', order: 'DESC' },
] as const;

const createFavoritesQuery = ({
  limit,
  cursor,
}: {
  limit: number;
  cursor?: string;
}) => ({
  query: {
    cursorPaging: { limit, cursor },
    sort: favoriteTemplatesSort,
  },
});

export async function addFavoriteTemplate(
  httpClient: HttpClient,
  entityId: string,
) {
  await httpClient.post(FAVORITES_ADD_API_URL, { entityId });
}

export async function removeFavoriteTemplate(
  httpClient: HttpClient,
  entityId: string,
) {
  await httpClient.post(FAVORITES_REMOVE_API_URL, { entityId });
}

export async function queryFavoriteTemplateIds(
  httpClient: HttpClient,
): Promise<string[]> {
  const favoriteIds: string[] = [];
  let cursor: string | undefined;
  let hasNext = true;

  while (hasNext) {
    const { data } = await httpClient.post<FavoritesResponse>(
      FAVORITES_QUERY_API_URL,
      createFavoritesQuery({ limit: FAVORITE_IDS_PAGE_SIZE, cursor }),
    );

    (data.favorites ?? []).forEach((favorite) => {
      const entityId = favorite.entity?.entityId;
      if (entityId) {
        favoriteIds.push(entityId);
      }
    });

    hasNext = Boolean(data.pagingMetadata?.hasNext);
    cursor = data.pagingMetadata?.cursors?.next ?? undefined;
  }

  return favoriteIds;
}

export async function countFavoriteTemplates(
  httpClient: HttpClient,
): Promise<number> {
  const { data } = await httpClient.post<{ count?: number }>(
    FAVORITES_COUNT_API_URL,
  );
  return data.count ?? 0;
}

export async function queryFavoriteTemplates({
  httpClient,
  cursor,
  pageSize,
}: {
  httpClient: HttpClient;
  cursor?: string;
  pageSize: number;
}) {
  const { data } = await httpClient.post<TemplateQueryResponse>(
    FAVORITES_TEMPLATES_API_URL,
    { query: { cursorPaging: { limit: pageSize, cursor } } },
  );

  return data;
}
